import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Icon, Modal, Text } from '@Sage/hoi-poi-ui';
import { SectionListCrud } from '@Sage/ssm-web5';
import { getLiteral } from 'utils/getLiteral';
import { formatDecimalByConfig, DECIMAL_TYPES, normalizeDecimalValue } from 'utils/decimal';
import { warningToast } from 'utils/toast';

import CONFIG from './TableConfig';
import FamilyRatesAddModal from './FamilyRatesAddModal';
import RatesTableSection from '../RatesTableSection';
import {
    useAppendToRatesModelList,
    useRatesModelListRef,
    useRatesTableOnChangeCell,
} from '../shared/ratesModelListUtils';

import './styles.scss';

const MAX_FAMILIES = 200;

const deleteModalOverrides = {
    confirmButton: {
        type: 'primary-error',
    },
};

const parseDiscount = (value) => {
    const parsed = normalizeDecimalValue(value);
    return parsed == null ? 0 : parsed;
};

// Helper to compute total discount from individual discounts
const computeTotalDiscount = (d1, d2, d3, d4) => {
    const factor = (1 - d1 / 100) * (1 - d2 / 100) * (1 - d3 / 100) * (1 - d4 / 100);
    return (1 - factor) * 100;
};

// Helper to format total discount for display
const formatTotalDiscount = ({ value, idEnvironment, idCurrency }) => {
    const formatted = formatDecimalByConfig({
        value,
        type: DECIMAL_TYPES.GENERAL,
        idEnvironment,
        idCurrency,
        fieldId: 'totaldiscount',
    });
    return formatted ? `${formatted}%` : '-';
};

const crudSchema = [
    {
        id: 'ratediscount1',
        type: 'percentage',
        mandatory: true,
        defaultValue: 0,
        postComponent: <span>%</span>,
    },
    {
        id: 'ratediscount2',
        type: 'percentage',
        mandatory: false,
        defaultValue: 0,
        postComponent: <span>%</span>,
    },
    {
        id: 'ratediscount3',
        type: 'percentage',
        mandatory: false,
        defaultValue: 0,
        postComponent: <span>%</span>,
    },
    {
        id: 'ratediscount4',
        type: 'percentage',
        mandatory: false,
        defaultValue: 0,
        postComponent: <span>%</span>,
    },
];

const CrudFamilyRates = memo(
    ({ changeField, errors, field = 'familyrates', idCurrency, idEnvironment, modelList }) => {
        const dataSize = modelList ? modelList.size : 0;

        const onChange = useCallback(
            (fieldKey, value) => {
                changeField?.(fieldKey)(value);
            },
            [changeField],
        );

        const modelListRef = useRatesModelListRef(modelList);
        const appendToModelList = useAppendToRatesModelList({ field, modelListRef, onChange });
        const { stableOnChangeCell } = useRatesTableOnChangeCell({ field, modelListRef, onChange });

        // ========================================
        // LOCAL STATE
        // ========================================
        const tableRef = useRef(null);
        const [open, setOpen] = useState(false);
        const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
        const [rowToDelete, setRowToDelete] = useState(null);

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const handleOnRef = useCallback((ref) => {
            tableRef.current = ref;
        }, []);

        const handleOpen = useCallback(() => {
            if (dataSize >= MAX_FAMILIES) {
                warningToast({
                    text: getLiteral('label_price_list_exceeds_families'),
                });
                return;
            }
            setOpen(true);
        }, [dataSize]);

        const handleClose = useCallback(() => {
            setOpen(false);
        }, []);

        const openDeleteDialog = useCallback((row) => {
            setRowToDelete(row);
            setDeleteDialogOpen(true);
        }, []);

        const closeDeleteDialog = useCallback(() => {
            setDeleteDialogOpen(false);
            setRowToDelete(null);
        }, []);

        const confirmDeleteLine = useCallback(() => {
            const list = modelListRef.current;
            if (!list || !onChange || !rowToDelete) return;
            const rowIndex = list.findIndex((item) => String(item.id) === String(rowToDelete.id));
            if (rowIndex !== -1) {
                onChange(field, list.delete(rowIndex));
            }
            closeDeleteDialog();
        }, [modelListRef, onChange, field, rowToDelete, closeDeleteDialog]);

        const getRowNodeId = useCallback((data) => String(data.id), []);

        // Derive rowData from Redux state - compute totaldiscount ourselves to avoid model getter bugs
        const rowData = useMemo(() => {
            if (!modelList) return [];
            return modelList.toArray().map((model, index) => {
                // Read discount values directly from entity to avoid model getter bugs
                const d1 = parseDiscount(model.entity?.ratediscount1);
                const d2 = parseDiscount(model.entity?.ratediscount2);
                const d3 = parseDiscount(model.entity?.ratediscount3);
                const d4 = parseDiscount(model.entity?.ratediscount4);
                const total = computeTotalDiscount(d1, d2, d3, d4);

                return {
                    id: model.id,
                    familyname: model.familyname,
                    numLine: index + 1,
                    ratediscount1: d1,
                    ratediscount2: d2,
                    ratediscount3: d3,
                    ratediscount4: d4,
                    totaldiscount: formatTotalDiscount({
                        value: total,
                        idEnvironment,
                        idCurrency,
                    }),
                };
            });
        }, [idCurrency, idEnvironment, modelList]);

        const getColumnDefs = useCallback(
            ({ transformTableToCrud }) => {
                return new Promise((resolve) => {
                    const config = CONFIG();
                    let columnDefs = config.columnDefs;

                    // Transform columns to CRUD mode using the provided function
                    columnDefs = transformTableToCrud({
                        columnDefs,
                        crudSchema,
                        inputProps: {
                            onChange: stableOnChangeCell,
                        },
                    });

                    resolve(columnDefs);
                });
            },
            [stableOnChangeCell],
        );

        const actions = useMemo(
            () => [
                (row) => ({
                    icon: <Icon name="close" onClick={() => openDeleteDialog(row)} />,
                    tooltip: { content: getLiteral('label_remove_from_list') },
                }),
            ],
            [openDeleteDialog],
        );

        const { domLayout, extraClassName } = useMemo(() => {
            if (!rowData?.length) {
                return {
                    domLayout: 'normal',
                    extraClassName: 'rates-crud-table--empty',
                };
            }
            if (rowData.length <= 5) {
                return { domLayout: 'autoHeight', extraClassName: '' };
            }
            return {
                domLayout: 'normal',
                extraClassName: 'rates-crud-table--scrollable',
            };
        }, [rowData]);

        return (
            <RatesTableSection
                addLabel={getLiteral('action_add')}
                className="familyRatesField"
                count={dataSize}
                emptyText={getLiteral('error_no_familiesadded_rate')}
                isEmpty={dataSize === 0}
                onAdd={handleOpen}
                sectionAdvice={getLiteral('helptext_per_families_rates')}
                sectionExtraText={getLiteral('label_info_families_rates')}
                sectionTitle={getLiteral('label_per_families_rates')}
                widgetTitle={getLiteral('label_family')}
                footer={
                    <>
                        <Modal
                            className="fm-delete-modal"
                            overlayClassName="fm-delete-modal__overlay"
                            overrides={deleteModalOverrides}
                            useCornerClose={false}
                            isOpen={deleteDialogOpen}
                            onCancel={closeDeleteDialog}
                            onConfirm={confirmDeleteLine}
                            title={getLiteral('label_remove_from_list')}
                            confirmText={getLiteral('action_confirm_bn')}
                            cancelText={getLiteral('action_cancel')}
                            size="small"
                        >
                            <Text>{getLiteral('confirm_sure')}</Text>
                        </Modal>
                        <FamilyRatesAddModal
                            title={getLiteral('action_search_families')}
                            handleClose={handleClose}
                            onAddLine={appendToModelList}
                            open={open}
                            familiesList={modelList}
                        />
                    </>
                }
            >
                <SectionListCrud
                    id="family-rates-table"
                    className={`rates-crud-table family-rates-table ${extraClassName}`}
                    errors={errors}
                    getColumnDefs={getColumnDefs}
                    idCurrency={idCurrency}
                    idEnvironment={idEnvironment}
                    rowData={rowData}
                    getRowNodeId={getRowNodeId}
                    useSort={false}
                    domLayout={domLayout}
                    actions={actions}
                    onRef={handleOnRef}
                    forceRowUpdating
                />
            </RatesTableSection>
        );
    },
);

CrudFamilyRates.displayName = 'CrudFamilyRates';

export default CrudFamilyRates;
